import React from 'react';
import includes from 'lodash/includes';

// constellation
import { Anchor, Flex, IconExternal, Spacer, Text } from '@zillow/constellation';

// styles
import { StyledMobileViewList } from './style';
import { StyledMessageTab } from '../../style';

import { getPaymentsPath, shouldDisplayDropdownLabelHelp, shouldDisplayResources } from '../../utils';

// constants
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';

// components
import Dropdown from '../Dropdown';
import { StyledAnchor, StyledNoTextDecorationTextButton } from '../DropdownMenu/style';

// interfaces
import type { HeaderSubNavLink } from '../../interfaces/HeaderSubNavLink';
import type { Displays } from '../../interfaces/Displays';
import type { Paths } from '@zg-rentals/paths-manifest';
import type { User } from '../../interfaces/user';

interface Props {
  paths: Paths;
  pathname: string;
  displays: Displays;
  userModel: User | null;
  onLoginClick?(): Promise<void> | void;
  headerSubNavLinks: Array<HeaderSubNavLink>;
  shouldShowHeaderSubNav: boolean;
  isZrmHomepage: boolean;
}

const MobileListView = ({
  displays,
  onLoginClick,
  headerSubNavLinks,
  paths,
  pathname,
  shouldShowHeaderSubNav,
  userModel,
  isZrmHomepage,
}: Props) => {
  const {
    helpPage,
    homePage,
    resourcesPage,
    mfhResourcesPage,
    settingsPage: securitySettingsPage,
    leadsPage,
    maintenanceTrackerPage,
    analyticsPage,
    analyticsOverview,
    billingPage,
    billingDue,
    zrmSignInPage: signInPageFunc,
    propertiesPage,
    userManagementPage,
    legacyZRM,
    contactSupportPage,
    postLogout,
    adManagementPage,
    setupTasks,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    rentPaymentPage,
  } = paths;

  const {
    displayAccessMainContactSupport,
    displayAnalytics,
    displayBilling,
    displayMainBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainLeads,
    displayMainHelp,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainSecuritySettings,
    displayUserManagement,
    displayMainResources,
    displayAdManagement,
    displayMainPayments,
  } = displays;

  const { loggedIn, paymentsAcceptedCount } = userModel ?? ({} as User);

  // functions that return paths
  const signInPage = signInPageFunc(pathname);

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  const renderMwebSubNav = () => {
    return headerSubNavLinks.map((subNavItem) => (
      <React.Fragment key={subNavItem.title}>
        {subNavItem.subTitles ? (
          <div className="MobileViewList-Link" key={subNavItem.title}>
            <Dropdown title={subNavItem.title}>
              {subNavItem.subTitles.map((subTitle) => (
                <Flex
                  key={subTitle.title}
                  display="flex"
                  justifyContent="flex-between"
                  marginLeft={0.5}
                  onClick={() => handleLinkClick(subNavItem.title)}
                >
                  <StyledAnchor title={subTitle.title} className="Link" href={subTitle.url}>
                    <StyledNoTextDecorationTextButton fontType="body">
                      {subTitle.title}
                    </StyledNoTextDecorationTextButton>
                  </StyledAnchor>
                </Flex>
              ))}
            </Dropdown>
          </div>
        ) : (
          <div className="MobileViewList-Link">
            <Flex display="flex" justifyContent="space-between" onClick={() => handleLinkClick(subNavItem.title)}>
              <StyledAnchor title={subNavItem.title} className="Link" href={subNavItem.url}>
                <Text fontType="body">{subNavItem.title}</Text>
              </StyledAnchor>
            </Flex>
          </div>
        )}
      </React.Fragment>
    ));
  };

  return (
    <StyledMobileViewList>
      {!loggedIn && (
        <div className="MobileViewList-Link">
          <div className="Link-signIn">
            <Anchor
              title="Sign in"
              className="Link"
              as="a"
              onClick={() => onLoginClick && onLoginClick()}
              // @ts-ignore
              href={signInPage}
            >
              <Text fontType="body">Sign In</Text>
            </Anchor>
          </div>
        </div>
      )}
      {loggedIn && displayMainMyProperties && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.myProperties)}>
          <StyledAnchor
            title={HeaderTitles.myProperties}
            className={includes(pathname, paths.propertiesPage) ? 'Link Link-active' : 'Link'}
            href={propertiesPage}
          >
            <Text fontType="body">{HeaderTitles.myProperties}</Text>
          </StyledAnchor>
        </div>
      )}
      {displayMainMessages && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.messages)}>
          <StyledMessageTab>
            <StyledAnchor
              title={HeaderTitles.messages}
              className={includes(pathname, 'inbox') ? 'Link Link-active' : 'Link'}
              href={legacyZRM.inbox}
            >
              <Text fontType="body">{HeaderTitles.messages}</Text>
            </StyledAnchor>
          </StyledMessageTab>
        </div>
      )}

      {displayMainMaintenanceTracker && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.messages)}>
          <StyledMessageTab>
            <StyledAnchor
              title={HeaderTitles.maintenanceTracker}
              className={includes(pathname, 'rental-manager/maintenance-tasks') ? 'Link Link-active' : 'Link'}
              href={maintenanceTrackerPage}
            >
              <Text fontType="body">{HeaderTitles.maintenanceTracker}</Text>
            </StyledAnchor>
          </StyledMessageTab>
        </div>
      )}

      {displayMainLeads && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.leads)}>
          <StyledAnchor
            title={HeaderTitles.leads}
            className={includes(pathname, 'leads') ? 'Link Link-active' : 'Link'}
            href={leadsPage}
          >
            <Text fontType="body">{HeaderTitles.leads}</Text>
          </StyledAnchor>
        </div>
      )}

      {displayAnalytics && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.analytics)}>
          <StyledAnchor
            title={HeaderTitles.analytics}
            className={includes(pathname, paths.analyticsPage) ? 'Link Link-active' : 'Link'}
            href={`${analyticsPage}/${analyticsOverview}`}
          >
            <Text fontType="body">{HeaderTitles.analytics}</Text>
          </StyledAnchor>
        </div>
      )}

      {displayMainBilling && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.billing)}>
          <StyledAnchor title={HeaderTitles.billing} className="Link" href={`${billingPage}/${billingDue}`}>
            <Text fontType="body">{HeaderTitles.billing}</Text>
          </StyledAnchor>
        </div>
      )}

      {loggedIn && displayMainPayments && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.payments)}>
          <StyledAnchor
            title={HeaderTitles.payments}
            className="Link"
            href={getPaymentsPath({
              paymentsAcceptedCount,
              paymentsAggregatePage,
              paymentsGetStartedPage,
              rentPaymentPage,
            })}
          >
            <Text fontType="body">{HeaderTitles.payments}</Text>
          </StyledAnchor>
        </div>
      )}

      {displayAdManagement && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.adManagement)}>
          <StyledAnchor title={HeaderTitles.adManagement} className="Link" href={`${adManagementPage}/${setupTasks}`}>
            <Text fontType="body">{HeaderTitles.adManagement}</Text>
          </StyledAnchor>
        </div>
      )}

      {displayUserManagement && (
        <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.userManagement)}>
          <StyledAnchor title={HeaderTitles.userManagement} className="Link" href={userManagementPage}>
            <Text fontType="body">{HeaderTitles.userManagement}</Text>
          </StyledAnchor>
        </div>
      )}

      {shouldShowHeaderSubNav && renderMwebSubNav()}

      {Boolean(shouldDisplayDropdownLabelHelp) && (
        <div className="MobileViewList-Link">
          <Dropdown title="Help">
            {shouldDisplayResources(displayMFHResources, displayMainResources) && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.resources)}
              >
                {displayMFHResources ? (
                  <StyledAnchor
                    title={HeaderTitles.mfhResources}
                    className="Link"
                    href={mfhResourcesPage}
                    target="_blank"
                  >
                    <StyledNoTextDecorationTextButton fontType="body">
                      {HeaderTitles.mfhResources}
                    </StyledNoTextDecorationTextButton>
                    <Spacer as="span" marginRight={0.5} />
                    <IconExternal fontColor="brand" />
                  </StyledAnchor>
                ) : (
                  <StyledAnchor title={HeaderTitles.resources} className="Link" href={resourcesPage} target="_blank">
                    <StyledNoTextDecorationTextButton fontType="body">
                      {HeaderTitles.resources}
                    </StyledNoTextDecorationTextButton>
                    <Spacer as="span" marginRight={0.5} />
                    <IconExternal fontColor="brand" />
                  </StyledAnchor>
                )}
              </Flex>
            )}

            {displayMainHelp && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.help)}
              >
                <StyledAnchor
                  title={HeaderTitles.help}
                  className="Link"
                  // @ts-ignore
                  href={helpPage}
                  target="_blank"
                >
                  <StyledNoTextDecorationTextButton fontType="body">
                    {HeaderTitles.help}
                  </StyledNoTextDecorationTextButton>
                  <Spacer as="span" marginRight={0.5} />
                  <IconExternal fontColor="brand" />
                </StyledAnchor>
              </Flex>
            )}

            {displayAccessMainContactSupport && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.contactSupport)}
              >
                <StyledAnchor
                  title={HeaderTitles.contactSupport}
                  className="Link"
                  href={contactSupportPage}
                  target="_blank"
                >
                  <StyledNoTextDecorationTextButton fontType="body">
                    {HeaderTitles.contactSupport}
                  </StyledNoTextDecorationTextButton>
                  <Spacer as="span" marginRight={0.5} />
                  <IconExternal fontColor="brand" />
                </StyledAnchor>
              </Flex>
            )}
          </Dropdown>
        </div>
      )}
      {loggedIn && (
        <div className="MobileViewList-Link">
          <Dropdown title={userModel?.email}>
            {displayMainAccountSettings && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.accountSettings)}
              >
                <StyledAnchor
                  title={HeaderTitles.accountSettings}
                  className="Link"
                  // @ts-ignore
                  href={paths.userSettingsPage}
                  target="_blank"
                >
                  <StyledNoTextDecorationTextButton fontType="body">
                    {HeaderTitles.accountSettings}
                  </StyledNoTextDecorationTextButton>
                  <Spacer as="span" marginRight={0.5} />
                  <IconExternal fontColor="brand" />
                </StyledAnchor>

                <Text fontType="finePrint">Managed on Zillow.com</Text>
              </Flex>
            )}

            {displayMainSecuritySettings && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.securitySettings)}
              >
                <StyledAnchor
                  title={HeaderTitles.securitySettings}
                  className="Link"
                  href={securitySettingsPage}
                  target="_blank"
                >
                  <StyledNoTextDecorationTextButton fontType="body">
                    {HeaderTitles.securitySettings}
                  </StyledNoTextDecorationTextButton>
                  <Spacer as="span" marginRight={0.5} />
                  <IconExternal fontColor="brand" />
                </StyledAnchor>
              </Flex>
            )}

            {displayBilling && !displayMainBilling && (
              <Flex
                display="flex"
                justifyContent="space-between"
                marginLeft={0.5}
                onClick={() => handleLinkClick(HeaderTitles.billing)}
              >
                <StyledAnchor title={HeaderTitles.billing} className="Link" href={`${billingPage}/${billingDue}`}>
                  <StyledNoTextDecorationTextButton fontType="body">
                    {HeaderTitles.billing}
                  </StyledNoTextDecorationTextButton>
                </StyledAnchor>
              </Flex>
            )}

            <Flex
              display="flex"
              justifyContent="space-between"
              marginLeft={0.5}
              onClick={() => handleLinkClick(HeaderTitles.signOut)}
            >
              <StyledAnchor
                title="Sign out"
                className="Link"
                // @ts-ignore
                href={postLogout}
              >
                <StyledNoTextDecorationTextButton fontType="body">Sign out</StyledNoTextDecorationTextButton>
              </StyledAnchor>
            </Flex>
          </Dropdown>
        </div>
      )}

      <div className="MobileViewList-Link" onClick={() => handleLinkClick(HeaderTitles.backToBrand)}>
        <StyledAnchor title="Go to Zillow" className="Link" href={homePage}>
          <Text fontType="body">Go to Zillow</Text>
        </StyledAnchor>
      </div>
    </StyledMobileViewList>
  );
};

export default MobileListView;
